import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';
import { usePermissionContext } from '../contexts/PermissionContext.js';

function AccountList({ onAdd, onViewProfile, onEdit, onSetBalance }) {
    const { hasPermission } = usePermissionContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [grandTotals, setGrandTotals] = useState({ debit: '0.00', credit: '0.00', balance: '0.00' });

    const fetchUnifiedAccounts = async (search = '', page = 1, limit = 10) => {
        try {
            if (!search && page === 1) setLoading(true);
            const data = await fetchApi(`/account/unified?search=${search}&page=${page}&limit=${limit}`);
            if (data && data.accounts) {
                setRows(data.accounts);
                setTotalEntries(data.total || 0);
                setTotalPages(data.pages || 1);
                if (data.grandTotals) setGrandTotals(data.grandTotals);
            }
        } catch (error) {
            console.error('Error fetching unified accounts:', error);
            toast.error("Failed to load account list");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchUnifiedAccounts(searchQuery, currentPage, itemsPerPage);
        }, 300);
        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, currentPage, itemsPerPage]);

    const [selectedIdx, setSelectedIdx] = useState(0);

    // Keyboard navigation: Up, Down, Enter
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (rows.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIdx((prev) => (prev + 1) % rows.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIdx((prev) => (prev - 1 + rows.length) % rows.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const selectedItem = rows[selectedIdx];
                if (selectedItem && onViewProfile) {
                    onViewProfile(selectedItem);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [rows, selectedIdx, onViewProfile]);

    const showingFrom = totalEntries === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const showingTo = Math.min(totalEntries, (currentPage - 1) * itemsPerPage + rows.length);

    const pages = useMemo(() => {
        const out = [];
        const maxButtons = 5;
        let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
        let end = Math.min(totalPages, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);
        for (let i = start; i <= end; i += 1) out.push(i);
        return out;
    }, [currentPage, totalPages]);

    if (loading) return <PageSkeleton />;

    const columns = [
        {
            key: 'partyName',
            label: 'PARTY A/C',
            render: (item, idx) => (
                <div
                    className={`party-name-box ${idx === selectedIdx ? 'party-name-box--active' : ''}`}
                    onClick={() => {
                        setSelectedIdx(idx);
                        onViewProfile && onViewProfile(item);
                    }}
                >
                    {item.partyName}
                </div>
            )
        },
        { key: 'group', label: 'GROUP' },
        { key: 'credit', label: 'CREDIT', render: (item) => `₹${item.credit || '0.00'}` },
        { key: 'debit', label: 'DEBIT', render: (item) => `₹${item.debit || '0.00'}` },
        {
            key: 'balance',
            label: 'BALANCE',
            style: { textAlign: 'right' },
            render: (item) => (
                <span style={{
                    fontWeight: '600',
                    color: ((item.group || '').toLowerCase().includes('bank') ||
                        (item.group || '').toLowerCase().includes('cash') ||
                        (item.partyName || '').toLowerCase().includes('bank') ||
                        (item.partyName || '').toLowerCase().includes('cash'))
                        ? '#000000'
                        : (parseFloat(item.balance || 0) < 0 ? '#dc2626' : '#16a34a')
                }}>
                    ₹{Math.abs(parseFloat(item.balance || 0)).toFixed(2)}
                </span>
            )
        },
        {
            key: 'actions',
            label: 'ACTION',
            style: { textAlign: 'center' },
            render: (item) => hasPermission('Account', 'update') ? (
                <button
                    className="list-page-btn"
                    style={{ backgroundColor: '#0b3a54', fontSize: '11px', padding: '6px 12px', color: '#ffffff', margin: '0 auto', display: 'block' }}
                    onClick={() => onSetBalance && onSetBalance(item)}
                >
                    Set Balance
                </button>
            ) : null
        }
    ];

    const footerRow = (
        <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
            <td colSpan={2} style={{ textAlign: 'right', padding: '12px' }}>GRAND TOTAL:</td>
            <td style={{ color: '#0b3a54' }}>₹{grandTotals.credit}</td>
            <td style={{ color: '#0b3a54' }}>₹{grandTotals.debit}</td>
            <td style={{
                textAlign: 'right',
                color: parseFloat(grandTotals.balance || 0) < 0 ? '#dc2626' : '#16a34a'
            }}>
                ₹{Math.abs(parseFloat(grandTotals.balance || 0)).toFixed(2)}
            </td>
            <td></td>
        </tr>
    );

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">Account List</h1>
                <div className="list-page-action-buttons">
                    {hasPermission('Account', 'add') && (
                        <button className="list-page-btn list-page-btn-add" onClick={onAdd}>
                            Add Account
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={rows}
                loading={loading}
                emptyMessage="No records found"
                page={currentPage}
                pageSize={itemsPerPage}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setItemsPerPage}
                footerRow={footerRow}
                onRowClick={(item, idx) => {
                    setSelectedIdx(idx);
                }}
                rowClassName={(item, idx) => idx === selectedIdx ? 'selected-row' : ''}
                searchBar={
                    <div style={{ marginLeft: 'auto' }}>
                        <SearchBar
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Search accounts..."
                        />
                    </div>
                }
            />
        </div>
    );
}

export default AccountList;
